import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Loader2, DatabaseZap, CheckCircle2, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Surfaces the lead → Companies/Contacts migration. Auto-runs a small sample
 * on first view so the user can review results, then offers a one-click full
 * migration (runs as a background job with live progress).
 */
const MigrationBanner = ({ onDone }) => {
  const [status, setStatus] = useState(null);
  const [sampling, setSampling] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const sampledRef = useRef(false);
  const pollRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/companies/backfill-status`);
      setStatus(res.data);
      return res.data;
    } catch { return null; }
  }, []);

  const runSample = useCallback(async () => {
    setSampling(true);
    try {
      await axios.post(`${API}/companies/backfill-from-leads`, null, { params: { limit: 20 } });
      await fetchStatus();
      if (onDone) onDone();
    } catch { toast.error('Sample migration failed'); }
    finally { setSampling(false); }
  }, [fetchStatus, onDone]);

  // On mount: fetch status; if no company exists yet but leads do, auto-run a sample once.
  useEffect(() => {
    let active = true;
    (async () => {
      const s = await fetchStatus();
      if (!active || !s) return;
      const job = s.job;
      const fullRunning = job && job.status === 'running';
      if (fullRunning) startPolling();
      if (!sampledRef.current && s.companies_total === 0 && s.leads_total > 0 && !fullRunning) {
        sampledRef.current = true;
        runSample();
      }
    })();
    return () => { active = false; if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const s = await fetchStatus();
      if (s && s.job && s.job.status === 'completed') {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setMigrating(false);
        toast.success(`Migration complete - ${s.companies_total.toLocaleString()} companies, ${s.contacts_total.toLocaleString()} contacts`);
        if (onDone) onDone();
      }
    }, 2500);
  }, [fetchStatus, onDone]);

  const runFull = useCallback(async () => {
    const remaining = (status?.leads_total || 0);
    if (!window.confirm(`Migrate all ${remaining.toLocaleString()} leads into Companies & Contacts? This runs in the background and is safe to re-run.`)) return;
    setMigrating(true);
    try {
      await axios.post(`${API}/companies/backfill-from-leads`, null, { params: { background: true } });
      toast.info('Full migration started in the background…');
      await fetchStatus();
      startPolling();
    } catch {
      toast.error('Could not start migration');
      setMigrating(false);
    }
  }, [status, fetchStatus, startPolling]);

  if (!status || dismissed) return null;

  const { companies_total, contacts_total, leads_total, job } = status;
  const fullRunning = (job && job.status === 'running') || migrating;
  const everMigrated = job && (job.status === 'completed') && job.total_leads;

  // Hide entirely if there are no leads to migrate.
  if (leads_total === 0) return null;

  // If a meaningful number of companies already exist and a full run completed, collapse to a small note.
  const looksDone = everMigrated && companies_total > 0;

  const progressPct = fullRunning && job?.total_leads
    ? Math.min(99, Math.round(((job.leads_scanned || 0) / job.total_leads) * 100))
    : 0;

  return (
    <div className="relative rounded-xl border border-violet-800/40 bg-violet-950/20 p-4" data-testid="migration-banner">
      <button
        onClick={() => setDismissed(true)}
        className="absolute top-3 right-3 text-slate-500 hover:text-slate-300"
        data-testid="dismiss-migration-btn"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-violet-600/20 flex items-center justify-center shrink-0">
          {looksDone ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> : <DatabaseZap className="w-5 h-5 text-violet-300" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-white">
            {looksDone ? 'Leads migrated to your CRM' : sampling ? 'Building a sample preview…' : 'Migrate your leads into Companies & Contacts'}
          </p>
          <p className="text-xs text-slate-400 mt-0.5">
            {leads_total.toLocaleString()} leads · {companies_total.toLocaleString()} companies · {contacts_total.toLocaleString()} contacts so far.
            {!looksDone && !fullRunning && ' Review the sample below, then run the full migration when ready.'}
          </p>

          {fullRunning && (
            <div className="mt-3 space-y-1.5" data-testid="migration-progress">
              <Progress value={progressPct} className="h-2" />
              <p className="text-[11px] text-slate-500">
                Migrating… {(job?.leads_scanned || 0).toLocaleString()} / {(job?.total_leads || leads_total).toLocaleString()} leads scanned
              </p>
            </div>
          )}

          {!fullRunning && (
            <div className="flex flex-wrap gap-2 mt-3">
              {!looksDone && (
                <Button size="sm" variant="outline" className="bg-slate-900 border-slate-700 text-slate-200" disabled={sampling} onClick={runSample} data-testid="run-sample-btn">
                  {sampling ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Sampling…</> : 'Run sample (20)'}
                </Button>
              )}
              <Button size="sm" className="bg-violet-600 hover:bg-violet-500" onClick={runFull} data-testid="run-full-migration-btn">
                {looksDone ? 'Re-run full migration' : 'Migrate all leads'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MigrationBanner;
